(() => {
  const SELECTORS = {
    widget: '[data-odp-payment-widget]',
    open: '[data-odp-payment-open]',
    modal: '[data-odp-payment-modal]',
    close: '[data-odp-payment-close]',
    tab: '[data-odp-payment-tab]',
    panel: '[data-odp-payment-panel]',
    payflexAmount: '[data-odp-payflex-amount]',
    happyPayAmount: '[data-odp-happypay-amount]'
  };

  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function formatZar(cents) {
    const amount = Number(cents || 0) / 100;

    try {
      return new Intl.NumberFormat('en-ZA', {
        style: 'currency',
        currency: 'ZAR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount).replace('ZAR', 'R');
    } catch (error) {
      return 'R ' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
  }

  function getFocusableElements(container) {
    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
      return !element.hasAttribute('disabled') && element.offsetParent !== null;
    });
  }

  function initPaymentWidget(widget) {
    if (widget.dataset.odpPaymentReady === 'true') return;
    widget.dataset.odpPaymentReady = 'true';

    const openButton = widget.querySelector(SELECTORS.open);
    const modal = widget.querySelector(SELECTORS.modal);
    const tabButtons = widget.querySelectorAll(SELECTORS.tab);
    const panels = widget.querySelectorAll(SELECTORS.panel);
    const payflexAmount = widget.querySelector(SELECTORS.payflexAmount);
    const happyPayAmount = widget.querySelector(SELECTORS.happyPayAmount);

    if (!openButton || !modal) return;

    let lastFocusedElement = null;
    let previousBodyPaddingRight = '';
    let previousBodyOverflow = '';

    function updateAmounts(newPriceCents) {
      const priceCents = parseInt(newPriceCents, 10) || 0;

      widget.dataset.productPriceCents = String(priceCents);

      if (payflexAmount) {
        payflexAmount.textContent = formatZar(Math.round(priceCents / 4));
      }

      if (happyPayAmount) {
        happyPayAmount.textContent = formatZar(Math.round(priceCents / 2));
      }
    }

    function lockPageScroll() {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

      previousBodyPaddingRight = document.body.style.paddingRight;
      previousBodyOverflow = document.body.style.overflow;

      document.documentElement.classList.add('odp-payx-lock');
      document.body.classList.add('odp-payx-lock');

      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }

      document.body.style.overflow = 'hidden';
    }

    function unlockPageScroll() {
      document.documentElement.classList.remove('odp-payx-lock');
      document.body.classList.remove('odp-payx-lock');

      document.body.style.paddingRight = previousBodyPaddingRight;
      document.body.style.overflow = previousBodyOverflow;
    }

    function openModal() {
      lastFocusedElement = document.activeElement;

      if (!modal.dataset.portalActive) {
        document.body.appendChild(modal);
        modal.dataset.portalActive = 'true';
      }

      modal.hidden = false;
      openButton.setAttribute('aria-expanded', 'true');
      lockPageScroll();

      const closeButton = modal.querySelector('.odp-payx-modal__close');
      const focusable = getFocusableElements(modal);

      window.requestAnimationFrame(() => {
        if (closeButton) {
          closeButton.focus();
        } else if (focusable.length) {
          focusable[0].focus();
        } else {
          modal.setAttribute('tabindex', '-1');
          modal.focus();
        }
      });
    }

    function closeModal() {
      modal.hidden = true;
      openButton.setAttribute('aria-expanded', 'false');
      unlockPageScroll();

      if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
        lastFocusedElement.focus();
      }
    }

    function activateTab(tabName) {
      tabButtons.forEach((button) => {
        const isActive = button.dataset.odpPaymentTab === tabName;

        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
        button.tabIndex = isActive ? 0 : -1;
      });

      panels.forEach((panel) => {
        const isActive = panel.dataset.odpPaymentPanel === tabName;

        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });
    }

    function handleTabKeyboard(event, button) {
      const buttons = Array.from(tabButtons);
      const currentIndex = buttons.indexOf(button);

      if (currentIndex === -1) return;

      let nextIndex = currentIndex;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        nextIndex = (currentIndex + 1) % buttons.length;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        nextIndex = (currentIndex - 1 + buttons.length) % buttons.length;
      } else if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = buttons.length - 1;
      } else {
        return;
      }

      event.preventDefault();

      const nextButton = buttons[nextIndex];
      nextButton.focus();
      activateTab(nextButton.dataset.odpPaymentTab);
    }

    function trapFocus(event) {
      if (event.key !== 'Tab') return;

      const focusable = getFocusableElements(modal);

      if (!focusable.length) {
        event.preventDefault();
        modal.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }

      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    openButton.addEventListener('click', openModal);

    modal.addEventListener('click', (event) => {
      const closeTarget = event.target.closest(SELECTORS.close);

      if (closeTarget) {
        event.preventDefault();
        closeModal();
      }
    });

    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
        return;
      }

      trapFocus(event);
    });

    tabButtons.forEach((button) => {
      button.addEventListener('click', () => {
        activateTab(button.dataset.odpPaymentTab);
      });

      button.addEventListener('keydown', (event) => {
        handleTabKeyboard(event, button);
      });
    });

    updateAmounts(widget.dataset.productPriceCents);

    window.OutdoorphotoPaymentOptions = window.OutdoorphotoPaymentOptions || {};

    window.OutdoorphotoPaymentOptions.update = (newPriceCents) => {
      document.querySelectorAll(SELECTORS.widget).forEach((paymentWidget) => {
        const payflex = paymentWidget.querySelector(SELECTORS.payflexAmount);
        const happyPay = paymentWidget.querySelector(SELECTORS.happyPayAmount);
        const priceCents = parseInt(newPriceCents, 10) || 0;

        paymentWidget.dataset.productPriceCents = String(priceCents);

        if (payflex) {
          payflex.textContent = formatZar(Math.round(priceCents / 4));
        }

        if (happyPay) {
          happyPay.textContent = formatZar(Math.round(priceCents / 2));
        }
      });
    };
  }

  function initAllPaymentWidgets() {
    document.querySelectorAll(SELECTORS.widget).forEach(initPaymentWidget);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllPaymentWidgets);
  } else {
    initAllPaymentWidgets();
  }

  document.addEventListener('shopify:section:load', initAllPaymentWidgets);
})();